/**
 * @file schemas.ts
 * @brief Zod schemas + DTO types for company-scoped Boardroom MeetingIntel.
 *
 * @details
 * Parses the RAW snake_case payload returned by the shared boardroom API
 * (`/api/boardroom/sessions`). The backend widened these endpoints so a
 * company_admin/admin reads + manages any meeting owned by a member of
 * their company (owner's `users.company_id` scope). We mirror the mobile
 * app's raw record shape rather than its camelCase normalization, keeping
 * every object `.passthrough()` so backend additions never break parsing.
 *
 * The list endpoint returns a slim payload (no full transcript / translations
 * / mindmap); the detail endpoint returns the full record. `MeetingDetail`
 * therefore extends `MeetingRow` with the heavy blobs.
 *
 * @dependencies
 * - zod: runtime validation of untrusted API payloads
 *
 * @references
 * - BACKEND/src/features/boardroom/routes.js (GET /sessions, GET /sessions/:id)
 * - BACKEND/src/features/boardroom/metrics.service.mjs (metrics shape)
 * - FRONTEND/src/screens/MeetingIntelligence/boardroomMeetingsStorage.ts (raw fields)
 * - features/company-meetings/api.ts (consumer)
 *
 * @author DES
 * @date 08-07-2026
 */

import { z } from "zod";
import { NotesDocSchema } from "./notesDoc";
import { MeetingStatusSchema } from "./meetingStatus.ts";

// ----------------------------------------------------------------------
// Primitive coercion helper
// Postgres numeric/bigint columns can arrive as strings over JSON; coerce
// them to numbers so the UI can format without per-field guards.
// Related: features/company-sessions/schemas.ts (same pattern)
// ----------------------------------------------------------------------
const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// ----------------------------------------------------------------------
// Status + metric primitives
// `status` mirrors BoardroomMeetingStatus; `metrics` mirrors the output of
// metrics.service.mjs (a metric is null when there wasn't enough data).
// ----------------------------------------------------------------------
export { MeetingStatusSchema, type MeetingStatus } from "./meetingStatus.ts";

export const MetricBadgeSchema = z.enum(["GOOD", "OKAY", "POOR"]);

/** A single computed analytic, or null when there wasn't enough data. */
export const MeetingMetricSchema = z
  .object({
    score: numericString,
    badge: MetricBadgeSchema,
    label: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable();

export type MeetingMetric = z.infer<typeof MeetingMetricSchema>;

export const MeetingMetricsSchema = z
  .object({
    version: numericString.optional(),
    computedAt: z.string().nullable().optional(),
    readScore: MeetingMetricSchema.optional(),
    engagement: MeetingMetricSchema.optional(),
    sentiment: MeetingMetricSchema.optional(),
    languageBridge: MeetingMetricSchema.optional(),
    clientBalance: MeetingMetricSchema.optional(),
    followThrough: MeetingMetricSchema.optional(),
    // What synthesis verification removed or corrected (29-09-2026,
    // BACKEND intelVerify.service.mjs). Absent on meetings generated before.
    intelVerification: z
      .object({
        checked: z.number().optional(),
        judged: z.number().optional(),
        supported: z.number().optional(),
        partial: z.number().optional(),
        unsupported: z.number().optional(),
        rescued: z.number().optional(),
        removed: z
          .array(
            z
              .object({
                kind: z.string().optional(),
                text: z.string().nullable().optional(),
                reason: z.string().nullable().optional(),
              })
              .passthrough(),
          )
          .optional(),
        downgraded: z
          .array(
            z
              .object({
                point: z.string().nullable().optional(),
                from: z.string().optional(),
                to: z.string().optional(),
                reason: z.string().nullable().optional(),
              })
              .passthrough(),
          )
          .optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();

export type MeetingMetrics = z.infer<typeof MeetingMetricsSchema>;

// ----------------------------------------------------------------------
// Nested JSONB collections (participants, speakers, bookmarks, todos, …)
// Kept permissive (`.passthrough()`, most fields optional) because these
// blobs evolve independently on the record/synthesis pipeline.
// ----------------------------------------------------------------------
export const MeetingParticipantSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    isExternal: z.boolean().optional(),
    // External guest who is a registered Verblizr user (email/voiceprint-matched)
    isRegisteredUser: z.boolean().optional(),
    profileImageUrl: z.string().nullable().optional(),
    /** B48: "guest" when the server blanked a stand-in address (or the
     *  guest has no email). Show "Guest", never an email in a name slot. */
    emailHint: z.string().nullable().optional(),
  })
  .passthrough();

export type MeetingParticipant = z.infer<typeof MeetingParticipantSchema>;

export const MeetingResolvedSpeakerSchema = z
  .object({
    speakerId: z.string().nullable().optional(),
    speaker_id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    isExternal: z.boolean().optional(),
    is_external: z.boolean().optional(),
    profileImageUrl: z.string().nullable().optional(),
    profile_image_url: z.string().nullable().optional(),
    label: z.string().nullable().optional(),
    voiceClipUrl: z.string().nullable().optional(),
    // Second voice sample from a different part of the meeting (10-07-2026) —
    // additive, absent on meetings diarized before it shipped.
    voiceClipUrl2: z.string().nullable().optional(),
    // Sounds like nobody invited (diarization); never given a suggestion.
    unknownVoice: z.boolean().optional(),
    // "Last one left" (B37, owner only, never applied): the one person on
    // this meeting's roster not yet matched to a voice. Read by
    // lastOneLeft.ts `serverSuggestion`.
    suggestedUser: z
      .object({
        name: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        userId: z.string().nullable().optional(),
        profileImageUrl: z.string().nullable().optional(),
        isExternal: z.boolean().optional(),
        isRegisteredUser: z.boolean().optional(),
        suggestedBy: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    resolvedUser: z
      .object({
        name: z.string().nullable().optional(),
        isExternal: z.boolean().optional(),
        profileImageUrl: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();

export type MeetingResolvedSpeaker = z.infer<
  typeof MeetingResolvedSpeakerSchema
>;

export const MeetingBookmarkSchema = z
  .object({
    id: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    timestamp: z.string().nullable().optional(),
    timeInSecs: numericString.nullable().optional(),
  })
  .passthrough();

/**
 * A verified span of the recording an intel item is grounded in. Supersedes the
 * bare `timeInSecs` second (which is still emitted, derived from this) — the
 * span knows where the evidence ENDS and how confident we are, which a single
 * integer never could. `confidence: "none"` means the moment could not be
 * located, and the UI must show an ordinal rather than invent a clock.
 * (02-08-2026)
 * Related: BACKEND/src/features/boardroom/anchor.service.mjs,
 *          docs/product/meeting-intel-evidence-anchor-plan.md
 */
export const MeetingEvidenceAnchorSchema = z
  .object({
    startSecs: numericString.nullable().optional(),
    endSecs: numericString.nullable().optional(),
    segmentId: z.string().nullable().optional(),
    quote: z.string().nullable().optional(),
    confidence: z.enum(["exact", "approx", "none"]).nullable().optional(),
    source: z
      .enum(["quote", "text", "bookmark", "llm-id", "none"])
      .nullable()
      .optional(),
    userFlagged: z.boolean().nullable().optional(),
    promotedFromBookmark: z.boolean().nullable().optional(),
  })
  .passthrough();

export type MeetingEvidenceAnchor = z.infer<typeof MeetingEvidenceAnchorSchema>;

// ----------------------------------------------------------------------
// Ask-your-meetings (03-08-2026)
// One shape for both scopes — cross-meeting and single-meeting differ only by
// whether `meetingId` was sent. Related: BACKEND meetingSearch.service.mjs,
// meetingAnswer.service.mjs
// ----------------------------------------------------------------------
export const MeetingSearchResultSchema = z
  .object({
    meetingId: z.string(),
    meetingTitle: z.string().nullable().optional(),
    startedAt: z.string().nullable().optional(),
    kind: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    /** Lets a result play the exact audio it came from. */
    anchor: MeetingEvidenceAnchorSchema.nullable().optional(),
    /** Signed and present only on CITED excerpts — see the search route. */
    recordingUrl: z.string().nullable().optional(),
    score: numericString.nullable().optional(),
  })
  .passthrough();

// One definition for the allowance, used by BOTH the search response and the
// standalone quota read — two copies would let the same payload be described
// differently the first time a field moved. (03-08-2026)
export const MeetingSearchQuotaSchema = z.object({
  limit: numericString.nullable().optional(),
  used: numericString.nullable().optional(),
  remaining: numericString.nullable().optional(),
  planCode: z.string().nullable().optional(),
  periodEnd: z.string().nullable().optional(),
  enforced: z.boolean().nullable().optional(),
  // B20 (24-10-2026): one-time Free. Absent on an older backend.
  resetMode: z.string().nullable().optional(),
  resetsAt: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});

export const MeetingSearchResponseSchema = z
  .object({
    success: z.boolean().optional(),
    query: z.string().nullable().optional(),
    scope: z.string().nullable().optional(),
    results: z.array(MeetingSearchResultSchema).default([]),
    /** `answered: false` is a NORMAL outcome — the corpus may not hold the
     *  answer, and saying so beats a confident near-miss. */
    answer: z
      .object({
        answered: z.boolean().default(false),
        text: z.string().nullable().optional(),
        citations: z.array(MeetingSearchResultSchema).default([]),
      })
      .nullable()
      .optional(),
    quota: MeetingSearchQuotaSchema.nullable().optional(),
  })
  .passthrough();

/** Standalone read of the allowance — same payload, no search spent. */
export const MeetingSearchQuotaResponseSchema = z
  .object({
    success: z.boolean().optional(),
    enabled: z.boolean().default(false),
    quota: MeetingSearchQuotaSchema.nullable().optional(),
  })
  .passthrough();

export type MeetingSearchResult = z.infer<typeof MeetingSearchResultSchema>;
export type MeetingSearchResponse = z.infer<typeof MeetingSearchResponseSchema>;
export type MeetingSearchQuota = z.infer<typeof MeetingSearchQuotaSchema>;

export const MeetingTodoSchema = z
  .object({
    id: z.string().nullable().optional(),
    task: z.string().nullable().optional(),
    /** "manual" — typed by a person, never extracted. Absent on model items. */
    source: z.string().nullable().optional(),
    createdByName: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    /** Stamped when a manual to-do is edited later. The row says who added it
     *  by hand, so an unmarked rewrite would make that line quietly untrue. */
    editedAt: z.string().nullable().optional(),
    editedByName: z.string().nullable().optional(),
    assigneeUserId: z.string().nullable().optional(),
    assigneeSpeakerId: z.string().nullable().optional(),
    /** Follow-up owner of a to-do for someone outside the company (05-10-2026):
     *  a company member who chases it. Absent = the meeting owner. */
    followUpUserId: z.string().nullable().optional(),
    /** Dismissed by the owner (05-10-2026): hidden from every list and
     *  count, kept in the meeting, restorable. AI to-dos only. Every count of
     *  open to-dos skips it (DES 07-10-2026, Notes page). */
    dismissedAt: z.string().nullable().optional(),
    dismissedByName: z.string().nullable().optional(),
    followUpName: z.string().nullable().optional(),
    followUpEmail: z.string().nullable().optional(),
    // Stamped deterministically by synthesis from the resolved roster. This —
    // NOT assigneeSpeakerId — is the key the toggle permission is decided on,
    // on both the server and the client. (04-08-2026)
    assigneeEmail: z.string().nullable().optional(),
    /** Signed by the API for the assignee's account — the only avatar source
     *  for a manual to-do given to someone who was never in the meeting. */
    assigneeAvatarUrl: z.string().nullable().optional(),
    completed: z.boolean().optional(),
    dueDate: z.string().nullable().optional(),
    delayRiskPercent: numericString.nullable().optional(),
    riskLevel: z.enum(["low", "medium", "high"]).nullable().optional(),
    riskAlertReason: z.string().nullable().optional(),
    // Transcript offset (seconds) where this task was assigned — emitted by the
    // synthesis LLM so the voice-stamp clip seeks to the real moment. (08-07-2026)
    timeInSecs: numericString.nullable().optional(),
    anchor: MeetingEvidenceAnchorSchema.nullable().optional(),
    // Last-action stamps written by PATCH …/todos/:todoId off the DB clock, not
    // the device's. Both timestamp keys are kept side by side — the merge only
    // overwrites the one being set — so read whichever matches the CURRENT
    // `completed`, or a stale `completedAt` will render as if the task were
    // still done. This is last-action state, not an audit log. Declared here
    // (they already arrived via passthrough) so the UI can be type-checked
    // against them. (04-08-2026)
    completedAt: z.string().nullable().optional(),
    uncompletedAt: z.string().nullable().optional(),
    completedBy: z.string().nullable().optional(),
    completedByName: z.string().nullable().optional(),
    completedByIsOwner: z.boolean().nullable().optional(),
    /** Stamped when the owner gives an AI to-do to someone else after the
     *  meeting; only the assignee changes, never the text. (28-09-2026) */
    reassignedAt: z.string().nullable().optional(),
    reassignedByName: z.string().nullable().optional(),
    /** To-do grouping (F1a, ADR-062): a heading from this meeting and a
     *  learned category. Absent until the meeting is labelled. `*Source` =
     *  "user" when a person set it (never relabelled). */
    topic: z.string().nullable().optional(),
    topicSource: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    categorySource: z.string().nullable().optional(),
    dueDateSource: z.string().nullable().optional(),
    /** B48 (02-11-2026): an AI to-do whose wording the owner changed. The
     *  first AI wording stays in `originalTask`, the evidence anchor is never
     *  touched. `edited` is true while the text differs from it. */
    edited: z.boolean().nullable().optional(),
    originalTask: z.string().nullable().optional(),
    originalDueDate: z.string().nullable().optional(),
    /** "guest" when the assignee's stand-in email was blanked. */
    assigneeEmailHint: z.string().nullable().optional(),
    /** Given with "Add someone" to a person outside the company. */
    assigneeExternal: z.boolean().nullable().optional(),
  })
  .passthrough();

export type MeetingTodo = z.infer<typeof MeetingTodoSchema>;

/** Diarized dialogue segment (raw shape from diarization.service.mjs). */
export const MeetingTranscriptRowSchema = z
  .object({
    // Segment id (`t_12`, `t_12_2` after a speaker move): what the
    // correction and speaker-move routes address a line by. (29-09-2026)
    id: z.string().nullable().optional(),
    speakerId: z.string().nullable().optional(),
    speaker: z.string().nullable().optional(),
    text: z.string().nullable().optional(),
    timestamp: z.string().nullable().optional(),
    isExternal: z.boolean().optional(),
    profileImageUrl: z.string().nullable().optional(),
    // Segment start (seconds) from diarization — drives per-speaker spoken time.
    startSecs: z.number().nullable().optional(),
    // Turn end + sentence spans (02-08-2026). A diarized turn can run minutes,
    // so these are what let a clip cover the sentence rather than the turn.
    endSecs: z.number().nullable().optional(),
    sentences: z
      .array(
        z
          .object({
            text: z.string().nullable().optional(),
            startSecs: z.number().nullable().optional(),
            endSecs: z.number().nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
  })
  .passthrough();

export type MeetingTranscriptRow = z.infer<typeof MeetingTranscriptRowSchema>;

export const MeetingMindmapNodeSchema = z
  .object({
    id: z.string(),
    label: z.string().nullable().optional(),
    type: z
      .enum(["root", "topic", "decision", "action", "risk"])
      .nullable()
      .optional(),
    parentId: z.string().nullable().optional(),
  })
  .passthrough();

export const MeetingMindmapSchema = z
  .object({
    rootId: z.string().nullable().optional(),
    nodes: z.array(MeetingMindmapNodeSchema).default([]),
  })
  .passthrough();

export type MeetingMindmap = z.infer<typeof MeetingMindmapSchema>;

// ----------------------------------------------------------------------
// List row — slim payload from GET /api/boardroom/sessions
// Excludes the heavy transcript/translations/mindmap blobs (detail refetch).
// `is_owner` / `can_delete` / `organised_by` drive the row's action gating.
// ----------------------------------------------------------------------
// ----------------------------------------------------------------------
// Who has it (sharing v2, DES 21-10-2026): the card's one quiet line.
// Every list item carries `sharing`, or null when the server could not
// read it (show no line). Absent on older servers. viewerRole:
// - owner / admin: `sharedCount` people besides the owner, the first 3 in
//   `people`; admin also gets `sharedBy` = the owner.
// - shared / invited / viewer: `sharedBy` (+ `sharedAt` for shared).
// Related: employee-boardroom/MeetingSharingLine.tsx
// ----------------------------------------------------------------------
export const MeetingSharingSchema = z
  .object({
    sharedCount: z.number().nullable().optional(),
    people: z
      .array(
        z
          .object({
            userId: z.string().nullable().optional(),
            firstName: z.string().nullable().optional(),
            avatarUrl: z.string().nullable().optional(),
            isExternal: z.boolean().nullable().optional(),
            via: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
    viewerRole: z.string().nullable().optional(),
    sharedBy: z
      .object({
        userId: z.string().nullable().optional(),
        firstName: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    sharedAt: z.string().nullable().optional(),
  })
  .passthrough();
export type MeetingSharing = z.infer<typeof MeetingSharingSchema>;

export const MeetingRowSchema = z
  .object({
    id: z.string(),
    owner_id: z.string().nullable().optional(),
    /** Whose meeting, on a row I don't own; null on my own (BACKEND b2c5fad,
     *  22-10-2026: "Ali's meeting" on the admin's Team list). */
    owner: z
      .object({
        userId: z.string().nullable().optional(),
        firstName: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    title: z.string().nullable().optional(),
    started_at: z.string().nullable().optional(),
    total_duration_seconds: numericString.nullable().optional(),
    recording_url: z.string().nullable().optional(),
    recording_size_bytes: numericString.nullable().optional(),
    participants: z.array(MeetingParticipantSchema).nullable().optional(),
    // Derived, display-only merged attendee list (resolved speakers ∪ roster,
    // deduped) — the robust "who was in this meeting". See meetingShape.service.
    // Prefer this over `participants` for display. (08-07-2026)
    attendees: z.array(MeetingParticipantSchema).nullable().optional(),
    target_languages: z.array(z.string()).nullable().optional(),
    bookmarks: z.array(MeetingBookmarkSchema).nullable().optional(),
    resolved_speakers: z
      .array(MeetingResolvedSpeakerSchema)
      .nullable()
      .optional(),
    // Whole-speaker swaps (02-10-2026): the audit trail, and which one (the
    // last) can still be undone. The undo snapshot itself is never sent.
    speaker_swaps: z
      .array(
        z
          .object({
            id: z.string(),
            from: z.object({ name: z.string().nullable().optional() }).passthrough().nullable().optional(),
            to: z.object({ name: z.string().nullable().optional() }).passthrough().nullable().optional(),
            byName: z.string().nullable().optional(),
            at: z.string().nullable().optional(),
            undoneAt: z.string().nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
    speaker_swap_undo_id: z.string().nullable().optional(),
    // Tolerant (18-10-2026): a status this build doesn't know yet (the backend
    // can ship a new one first) reads as "error" instead of failing the whole
    // meetings list on web and the Mac app.
    status: MeetingStatusSchema.catch("error"),
    // Why an errored meeting stopped (stuck-meeting timeout, 18-10-2026):
    // no_speech | timeout | processing. See ../meetingFailure.ts.
    failure_reason: z.string().nullable().optional(),
    failed_at: z.string().nullable().optional(),
    key_decisions: z.array(z.string()).nullable().optional(),
    // Key takeaways — info that is neither a decision nor a todo; each carries
    // its own transcript offset for voice-stamp playback. (10-07-2026)
    key_points: z
      .array(
        z
          .object({
            point: z.string().nullable().optional(),
            // Mindmap topic label (13-10-2026, keyPointGroups.ts); absent on
            // meetings made before, which render as a flat list.
            topic: z.string().nullable().optional(),
            timeInSecs: numericString.nullable().optional(),
            anchor: MeetingEvidenceAnchorSchema.nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
    // What the owner planned vs what happened (19-08-2026). Present ONLY on the
    // owner's own meetings — the API strips it for every other reader, since it
    // derives from their private prep note. Absent (not empty) when no agenda
    // was attached, which is a different state from an agenda entirely missed.
    // Related: docs/decisions/ADR-023-note-meeting-links.md
    agenda_coverage: z
      .array(
        z
          .object({
            point: z.string().nullable().optional(),
            // The answer when the point asked something (29-09-2026).
            answer: z.string().nullable().optional(),
            noteId: z.string().nullable().optional(),
            noteTitle: z.string().nullable().optional(),
            status: z
              .enum(["covered_decided", "discussed_open", "not_raised"])
              .nullable()
              .optional(),
            timeInSecs: numericString.nullable().optional(),
            anchor: MeetingEvidenceAnchorSchema.nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
    // B49 (02-11-2026): the agenda ticked during the meeting, beside the AI's
    // verdict. null = no check boxes were used. Owner: every item + the AI
    // verdict and `match`; anyone else (`ownerView: false`): ticked items only,
    // `ai` / `match` null. Read only: nothing writes it after the upload.
    // Related: docs/product/agenda-checks-b49.md
    agenda_check: z
      .object({
        ownerView: z.boolean().nullable().optional(),
        total: z.number().nullable().optional(),
        tickedCount: z.number().nullable().optional(),
        items: z
          .array(
            z
              .object({
                noteId: z.string().nullable().optional(),
                noteTitle: z.string().nullable().optional(),
                index: z.number().nullable().optional(),
                text: z.string().nullable().optional(),
                ticked: z.boolean().nullable().optional(),
                tickedAtSecs: numericString.nullable().optional(),
                tickedAt: z.string().nullable().optional(),
                ai: z
                  .object({
                    status: z
                      .enum(["covered_decided", "discussed_open", "not_raised"])
                      .nullable()
                      .optional(),
                    timeInSecs: numericString.nullable().optional(),
                    answer: z.string().nullable().optional(),
                  })
                  .passthrough()
                  .nullable()
                  .optional(),
                match: z.string().nullable().optional(),
                notInRecorder: z.boolean().nullable().optional(),
              })
              .passthrough(),
          )
          .nullable()
          .optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    // Parallel to key_decisions (same length/order): transcript offset (seconds)
    // per decision, emitted by the synthesis LLM. null where unknown. (08-07-2026)
    key_decision_stamps: z.array(numericString.nullable()).nullable().optional(),
    // Parallel to key_decisions: the verified evidence span per decision. Wins
    // over key_decision_stamps wherever present. (02-08-2026)
    key_decision_anchors: z
      .array(MeetingEvidenceAnchorSchema.nullable())
      .nullable()
      .optional(),
    attention_risks: z.string().nullable().optional(),
    todos: z.array(MeetingTodoSchema).nullable().optional(),
    meeting_overview: z.string().nullable().optional(),
    invoice_url: z.string().nullable().optional(),
    is_invoiced: z.boolean().nullable().optional(),
    mom_pdf_url: z.string().nullable().optional(),
    metrics: MeetingMetricsSchema.nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    is_owner: z.boolean().nullable().optional(),
    organised_by: z.string().nullable().optional(),
    started_by: z.string().nullable().optional(),
    can_delete: z.boolean().nullable().optional(),
    // Meeting Notes vs full Meeting Intelligence (ADR-030, 06-10-2026). A
    // Notes meeting is private to its owner and has a summary, to-dos and the
    // transcript only. Absent from older servers = full.
    // Related: MeetingNotesTierView.tsx, isNotesTierMeeting below
    tier: z.enum(["notes", "full"]).nullable().optional().catch(null),
    upgraded_at: z.string().nullable().optional(),
    // Who has it (sharing v2, 21-10-2026). A malformed block reads as none.
    sharing: MeetingSharingSchema.nullable().optional().catch(null),
    // Company data controls (ADR-051, 21-10-2026): exports are off for this
    // viewer (the owner's company turned them off); no stored PDF links.
    exports_disabled: z.boolean().nullable().optional().catch(null),
  })
  .passthrough();

/** A Meeting Notes recording (ADR-030). Anything but exactly 'notes' is full,
 *  the same rule the server applies on create. */
export function isNotesTierMeeting(m: { tier?: string | null } | null | undefined): boolean {
  return m?.tier === "notes";
}

export type MeetingRow = z.infer<typeof MeetingRowSchema>;

// ----------------------------------------------------------------------
// Detail — full record from GET /api/boardroom/sessions/:id
// Extends the list row with the heavy blobs the detail page renders.
// ----------------------------------------------------------------------
export const MeetingDetailSchema = MeetingRowSchema.extend({
  meeting_summary: z.string().nullable().optional(),
  summary_overview: z.string().nullable().optional(),
  transcript: z.array(MeetingTranscriptRowSchema).nullable().optional(),
  translations: z.record(z.unknown()).nullable().optional(),
  meeting_mindmap: MeetingMindmapSchema.nullable().optional(),
  // Meeting Notes v2 (DES 09-10-2026, backend feat/notes-v2): sectioned notes
  // with labels, open questions and next steps. Absent from older servers and
  // from v1 meetings; an undrawable doc parses as null (notesDoc.ts).
  notes_doc: NotesDocSchema.optional(),
  // Owner only (12-10-2026): todoId → names of people who can see that one
  // to-do, never the meeting (to-do-only access, My to-dos).
  todo_only_access: z.record(z.array(z.string())).nullable().optional(),
  // B48 (02-11-2026): people given a to-do on this meeting with "Add
  // someone" (not on the roster: they were not in the room). Offered in the
  // Assign-to picker after the speakers.
  external_assignees: z
    .array(
      z
        .object({
          name: z.string().nullable().optional(),
          email: z.string().nullable().optional(),
          userId: z.string().nullable().optional(),
          profileImageUrl: z.string().nullable().optional(),
        })
        .passthrough(),
    )
    .nullable()
    .optional(),
}).passthrough();

export type MeetingDetail = z.infer<typeof MeetingDetailSchema>;

// ----------------------------------------------------------------------
// Response envelopes
// ----------------------------------------------------------------------
export const MeetingsListResponseSchema = z.object({
  success: z.boolean().optional(),
  meetings: z.array(MeetingRowSchema).default([]),
  page: numericString.optional(),
  pageSize: numericString.optional(),
  total: numericString.optional(),
  hasMore: z.boolean().optional(),
  /** "Mine | Team" (22-10-2026): the scope applied, and whether Team means
   *  anything for this user (company admins). */
  scope: z.string().optional(),
  teamScopeAvailable: z.boolean().optional(),
});

export type MeetingsListResponse = z.infer<typeof MeetingsListResponseSchema>;

export const MeetingDetailResponseSchema = z.object({
  success: z.boolean().optional(),
  meeting: MeetingDetailSchema,
});

/**
 * `PATCH …/todos/:todoId`: the whole meeting for someone who can see it, or
 * only `{ todo: { id, completed } }` for a to-do-only user (12-10-2026). A
 * meeting that fails to parse is treated as absent rather than an error: the
 * tick itself succeeded.
 */
export const ToggleTodoResponseSchema = z
  .object({
    success: z.boolean().optional(),
    meeting: MeetingDetailSchema.optional().catch(undefined),
    todo: z
      .object({ id: z.string().optional(), completed: z.boolean().optional() })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();

export const ExportMomResponseSchema = z.object({
  success: z.boolean().optional(),
  momPdfUrl: z.string(),
  cached: z.boolean().optional(),
});

export type ExportMomResponse = z.infer<typeof ExportMomResponseSchema>;

export const ExportTranscriptResponseSchema = z.object({
  success: z.boolean().optional(),
  transcriptPdfUrl: z.string(),
  cached: z.boolean().optional(),
  langKey: z.string().optional(),
});

export type ExportTranscriptResponse = z.infer<
  typeof ExportTranscriptResponseSchema
>;

export const TranslateMomResponseSchema = z
  .object({
    success: z.boolean().optional(),
    complete: z.boolean().optional(),
    scope: z.string().optional(),
    meetingOverview: z.string().nullable().optional(),
    meetingSummary: z.string().nullable().optional(),
    summaryOverview: z.string().nullable().optional(),
    keyDecisions: z.array(z.string()).nullable().optional(),
    keyPoints: z
      .array(
        z
          .object({
            point: z.string().nullable().optional(),
            topic: z.string().nullable().optional(),
            timeInSecs: numericString.nullable().optional(),
          })
          .passthrough(),
      )
      .nullable()
      .optional(),
    attentionRisks: z.string().nullable().optional(),
    todos: z.array(MeetingTodoSchema).nullable().optional(),
  })
  .passthrough();

export type TranslateMomResponse = z.infer<typeof TranslateMomResponseSchema>;

// ----------------------------------------------------------------------
// Notes attached to a meeting — GET /sessions/:id/prep-notes (21-08-2026)
//
// The link table read from the MEETING's side (ADR-023). Owner-only on the
// server, reads included, so a non-owner gets a 404 rather than an empty list —
// the query treats that as "nothing to show", which is the same outcome.
// Related: docs/decisions/ADR-023-note-meeting-links.md
// ----------------------------------------------------------------------
export const MeetingAttachedNoteSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable().optional(),
    // 'prep' was attached before synthesis and fed the agenda coverage;
    // 'addendum' was attached afterwards and is stored only.
    role: z.enum(["prep", "addendum"]).nullable().optional(),
    source: z.enum(["voice", "typed"]).nullable().optional(),
    transcript_text: z.string().nullable().optional(),
    // A recording's points too, since ADR-025 (newline-joined). Reading only
    // `transcript_text` here showed a spoken note's raw transcript where its
    // points belong.
    points: z.string().nullable().optional(),
    // Sub-points (DES 12-10-2026): 0 / 1 per point, aligned with `points`;
    // read through `readPointLevels`, never raw.
    point_levels: z.unknown().nullable().optional(),
    linked_at: z.string().nullable().optional(),
    // Who wrote it (ADR-024). A meeting's participants each attach their own
    // follow-ups, and a contribution without an author is an anonymous line in
    // a pile — the one thing worth knowing, dropped.
    owner_id: z.string().nullable().optional(),
    author_first_name: z.string().nullable().optional(),
    author_last_name: z.string().nullable().optional(),
    author_email: z.string().nullable().optional(),
    author_image_url: z.string().nullable().optional(),
    // False = only its author can see it.
    shared: z.boolean().nullable().optional(),
  })
  .passthrough();



export const MeetingAttachedNotesResponseSchema = z
  .object({
    success: z.boolean().optional(),
    notes: z.array(MeetingAttachedNoteSchema).nullable().optional(),
  })
  .passthrough();

export type MeetingAttachedNote = z.infer<typeof MeetingAttachedNoteSchema>;

/** Display name for a contribution's author. Falls back to the email rather
 *  than to "Unknown": on a contribution the name is the point, and an email
 *  still identifies a colleague. */
export function attachedNoteAuthorName(note: MeetingAttachedNote): string {
  const full = [note.author_first_name, note.author_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || (note.author_email ?? "").trim() || "Someone";
}

// ----------------------------------------------------------------------
// List query params (mirrors the backend's ?page=&pageSize=&search=&from=
// &to=&employee_id=). `page` here is 1-based; api.ts maps it to the wire.
// ----------------------------------------------------------------------
export type CompanyMeetingsListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  from?: string;
  to?: string;
  employee_id?: string;
  /** ADR-030 (06-10-2026): omitted = full meetings only (the Meetings lists);
   *  'notes' = the caller's own Meeting Notes (the Notes page); 'all' = both. */
  tier?: "notes" | "all";
  /** "Mine | Team" (22-10-2026), company admins only; omitted = unchanged. */
  scope?: "mine" | "team";
};
